import type { Edge, Node } from '@xyflow/react';

export type TaskCategory = 'human' | 'system' | 'control' | 'ai' | 'unknown';

export interface TaskNodeData extends Record<string, unknown> {
  xsiType: string;
  name: string;
  title?: string;
  isStart: boolean;
  inline: boolean;
  category: TaskCategory;
  assignedToGroup?: string;
  priority?: number;
  deadline?: string;
  extras: Array<{ label: string; value: string }>;
  rawXml: string;
}

export interface ConditionNodeData extends Record<string, unknown> {
  expression: string;
}

export interface ParseResult {
  nodes: Node[];
  edges: Edge[];
  diagnostics: string[];
  taskflowName: string;
}

const HUMAN_TYPES = new Set([
  'formTask',
  'questionTask',
  'judgePreApplicationTask',
  'verifyAccountTask',
  'authorizationTask',
  'confirmByApplicantTask',
  'waitForSubmitByApplicantTask',
]);

const CONTROL_TYPES = new Set([
  'genericTask',
  'startTaskflowTask',
]);

const AI_TYPES = new Set(['AITask']);

function categorize(xsiType: string): TaskCategory {
  if (HUMAN_TYPES.has(xsiType)) return 'human';
  if (AI_TYPES.has(xsiType)) return 'ai';
  if (CONTROL_TYPES.has(xsiType)) return 'control';
  if (!xsiType) return 'unknown';
  return 'system';
}

function localName(el: Element): string {
  return el.localName || el.tagName.replace(/^.*:/, '');
}

function childrenByLocalName(parent: Element, name: string): Element[] {
  const result: Element[] = [];
  for (const child of Array.from(parent.children)) {
    if (localName(child) === name) {
      result.push(child);
    }
  }
  return result;
}

function firstChildByLocalName(parent: Element, name: string): Element | undefined {
  for (const child of Array.from(parent.children)) {
    if (localName(child) === name) {
      return child;
    }
  }
  return undefined;
}

function textOf(parent: Element, name: string): string | undefined {
  const child = firstChildByLocalName(parent, name);
  return child?.textContent?.trim() || undefined;
}

function getXsiType(el: Element): string {
  const attrs = el.attributes;
  for (let i = 0; i < attrs.length; i++) {
    const a = attrs[i];
    if (a.localName === 'type' && (a.namespaceURI || '').includes('XMLSchema-instance')) {
      return a.value.replace(/^.*:/, '');
    }
  }
  const direct = el.getAttribute('xsi:type');
  return direct ? direct.replace(/^.*:/, '') : '';
}

function deadlineSummary(taskEl: Element): string | undefined {
  const abs = firstChildByLocalName(taskEl, 'absoluteDeadline');
  if (abs) {
    const v = textOf(abs, 'value');
    return v ? `due ${v.substring(0, 10)}` : 'absolute deadline';
  }
  const rel = firstChildByLocalName(taskEl, 'relativeDeadline');
  if (rel) {
    const unit = textOf(rel, 'unit');
    const value = textOf(rel, 'value');
    if (unit && value) return `+${value} ${unit}`;
  }
  return undefined;
}

function extrasFor(xsiType: string, taskEl: Element): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  const fields: Record<string, string[]> = {
    formTask: ['formDefinitionName'],
    sendEmailTask: ['templateName', 'recipientGroup', 'autoSend'],
    sendApplicantMessageTask: ['templateName', 'autoSend'],
    setStateTask: ['targetState'],
    generateDocumentTask: ['templateName', 'fileName'],
    generateFolderTask: ['folderPath'],
    copyAttachmentsTask: ['folderPath'],
    questionTask: ['question'],
    genericTask: ['description', 'autoExecute'],
    generatePaymentTask: ['taskflowName'],
    startTaskflowTask: ['taskflowName'],
    setTeamMemberTask: ['targetUserGroup', 'targetUser'],
    AITask: ['prompt', 'output'],
  };
  const list = fields[xsiType] ?? [];
  for (const f of list) {
    const v = textOf(taskEl, f);
    if (v) out.push({ label: f, value: v.length > 60 ? v.slice(0, 60) + '…' : v });
  }
  return out;
}

interface BuildContext {
  nodes: Node[];
  edges: Edge[];
  diagnostics: string[];
  startTaskName?: string;
  registered: Map<string, Node>; // by task name
  inlineCounter: { value: number };
  edgeCounter: { value: number };
}

function nextEdgeId(ctx: BuildContext): string {
  ctx.edgeCounter.value += 1;
  return `e${ctx.edgeCounter.value}`;
}

function buildTaskNode(taskEl: Element, inline: boolean, ctx: BuildContext): Node {
  const xsiType = getXsiType(taskEl);
  const name = textOf(taskEl, 'name') ?? `task-${ctx.inlineCounter.value++}`;
  const title = textOf(taskEl, 'title');
  const data: TaskNodeData = {
    xsiType: xsiType || 'unknown',
    name,
    title,
    isStart: !!ctx.startTaskName && ctx.startTaskName === name,
    inline,
    category: categorize(xsiType),
    assignedToGroup: textOf(taskEl, 'assignedToGroup'),
    priority: textOf(taskEl, 'priority') ? parseInt(textOf(taskEl, 'priority')!, 10) : undefined,
    deadline: deadlineSummary(taskEl),
    extras: extrasFor(xsiType, taskEl),
    rawXml: new XMLSerializer().serializeToString(taskEl),
  };
  const id = inline ? `inline:${name}:${ctx.inlineCounter.value++}` : `task:${name}`;
  const node: Node = {
    id,
    type: 'taskNode',
    data,
    position: { x: 0, y: 0 },
  };
  return node;
}

// Recursively walk a taskResult (used by action.next, condition.true, condition.false)
// Returns the target node id (creating an inline node if needed) or null if unresolved.
function resolveTaskResult(taskResult: Element | undefined, ctx: BuildContext): string | null {
  if (!taskResult) return null;
  const ref = textOf(taskResult, 'taskReference');
  if (ref) {
    const target = ctx.registered.get(ref);
    if (target) return target.id;
    // Unresolved: create a ghost node
    const ghostId = `ghost:${ref}`;
    if (!ctx.nodes.find(n => n.id === ghostId)) {
      ctx.nodes.push({
        id: ghostId,
        type: 'taskNode',
        data: {
          xsiType: 'unresolved',
          name: ref,
          title: `(unresolved) ${ref}`,
          isStart: false,
          inline: false,
          category: 'unknown',
          extras: [],
          rawXml: `<taskReference>${ref}</taskReference>`,
        } satisfies TaskNodeData,
        position: { x: 0, y: 0 },
      });
      ctx.diagnostics.push(`Unresolved taskReference: ${ref}`);
    }
    return ghostId;
  }
  const nestedTask = firstChildByLocalName(taskResult, 'task');
  if (nestedTask) {
    const inlineNode = buildTaskNode(nestedTask, true, ctx);
    ctx.nodes.push(inlineNode);
    walkActions(nestedTask, inlineNode.id, ctx);
    return inlineNode.id;
  }
  return null;
}

function walkActions(taskEl: Element, fromNodeId: string, ctx: BuildContext): void {
  const actions = childrenByLocalName(taskEl, 'action');
  for (const action of actions) {
    const actionName = textOf(action, 'name') ?? '';
    const actionTitle = textOf(action, 'title') ?? actionName;

    // Direct next
    const nextEl = firstChildByLocalName(action, 'next');
    const nextTargetId = resolveTaskResult(nextEl, ctx);
    if (nextTargetId) {
      ctx.edges.push({
        id: nextEdgeId(ctx),
        source: fromNodeId,
        target: nextTargetId,
        label: actionTitle,
        type: 'smoothstep',
        data: { kind: 'next' },
      });
    }

    // Conditions
    const conditions = childrenByLocalName(action, 'condition');
    for (const cond of conditions) {
      const expression = textOf(cond, 'expression') ?? '';
      const condId = `cond:${fromNodeId}:${actionName}:${ctx.inlineCounter.value++}`;
      ctx.nodes.push({
        id: condId,
        type: 'conditionNode',
        data: { expression } satisfies ConditionNodeData,
        position: { x: 0, y: 0 },
      });
      ctx.edges.push({
        id: nextEdgeId(ctx),
        source: fromNodeId,
        target: condId,
        label: actionTitle,
        type: 'smoothstep',
        data: { kind: 'next' },
      });

      const trueEl = firstChildByLocalName(cond, 'true');
      const trueTargetId = resolveTaskResult(trueEl, ctx);
      if (trueTargetId) {
        ctx.edges.push({
          id: nextEdgeId(ctx),
          source: condId,
          target: trueTargetId,
          label: 'true',
          type: 'smoothstep',
          data: { kind: 'condition-true' },
          style: { stroke: '#198754', strokeWidth: 2 },
          labelStyle: { fill: '#198754', fontWeight: 600 },
        });
      }
      const falseEl = firstChildByLocalName(cond, 'false');
      const falseTargetId = resolveTaskResult(falseEl, ctx);
      if (falseTargetId) {
        ctx.edges.push({
          id: nextEdgeId(ctx),
          source: condId,
          target: falseTargetId,
          label: 'false',
          type: 'smoothstep',
          data: { kind: 'condition-false' },
          style: { stroke: '#dc3545', strokeWidth: 2 },
          labelStyle: { fill: '#dc3545', fontWeight: 600 },
        });
      }
    }
  }
}

export function parseTaskflowXml(xml: string): ParseResult {
  const result: ParseResult = { nodes: [], edges: [], diagnostics: [], taskflowName: '' };
  if (!xml || !xml.trim()) {
    result.diagnostics.push('XML is empty');
    return result;
  }

  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    result.diagnostics.push('XML parse error: ' + (parserError.textContent ?? '').slice(0, 200));
    return result;
  }
  const root = doc.documentElement;
  if (!root || localName(root) !== 'taskflowDefinition') {
    result.diagnostics.push('Root element is not <taskflowDefinition>');
    return result;
  }
  result.taskflowName = textOf(root, 'name') ?? '';
  const startTaskName = textOf(root, 'startTaskName');

  const ctx: BuildContext = {
    nodes: result.nodes,
    edges: result.edges,
    diagnostics: result.diagnostics,
    startTaskName,
    registered: new Map(),
    inlineCounter: { value: 0 },
    edgeCounter: { value: 0 },
  };

  // Pass 1: register all top-level tasks so taskReferences can resolve
  const topTasks = childrenByLocalName(root, 'task');
  for (const taskEl of topTasks) {
    const node = buildTaskNode(taskEl, false, ctx);
    ctx.nodes.push(node);
    const name = (node.data as TaskNodeData).name;
    if (ctx.registered.has(name)) {
      ctx.diagnostics.push(`Duplicate task name: ${name}`);
    }
    ctx.registered.set(name, node);
  }

  // Pass 2: walk actions and build edges (inline tasks & conditions emerge here)
  for (const taskEl of topTasks) {
    const name = textOf(taskEl, 'name');
    if (!name) continue;
    const node = ctx.registered.get(name);
    if (!node) continue;
    walkActions(taskEl, node.id, ctx);
  }

  return result;
}
